/**
 * <GlobalBackButton/> — a single back chevron that appears top-left on
 * every non-immersive route, so users always have a clear way back.
 *
 * Hides on:
 *   • the home page itself ("/" / "/home")
 *   • any immersive route (login, journeys, vision board, sessions etc.)
 *   • routes that already render their own header back-button
 *
 * Behaviour:
 *   • If history length > 1 → router.back()
 *   • Else → navigate("/")
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { smartBack, parentLabelFor } from "../utils/smartBack";

// Routes where Global Back must NOT show — either they're top-level (no
// "back" makes sense) or they already have a back button rendered as
// part of the screen.
const HIDE_ON_EXACT = new Set([
  "/", "/home", "/login", "/register", "/forgot-password",
]);

const HIDE_PREFIXES = [
  "/login", "/register", "/forgot-password",
  "/upgrade", "/payment",
  "/listen", "/gift", "/gift-moment", "/share/audio",
  "/brand", "/brand-preview",
  "/networker/performance-vision/setup",
  "/networker/onboarding",
  // True immersive sessions (no overlay chrome):
  "/practices/state-tuning/play",
  "/practices/tapping/play",
  "/networker/prime/session",
  "/playback", "/immersion",
  "/abundance/living-vision-board",
  "/abundance/immerse",                // StateImmersion has its own Exit chip
  "/integration-program/session",
  // Pages that already render their own visible Back button:
  "/practices",
  "/networker/prime",
  "/admin/excelior-training",
  "/reclaim-presence",
  "/journey", "/journeys",
  "/integration-program",
  "/healing",
  "/balance",
  "/bank-of-reclaim",
  "/aurum-edge",
  "/golf",
  "/performance-vision",
  "/living-dream",
  "/atelier",
  "/gift/atelier",
  "/heart-ru",
  "/spine",
  "/heart/dad",
];

export default function GlobalBackButton() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [immersiveBodyFlag, setImmersiveBodyFlag] = React.useState(
    typeof document !== 'undefined' && document.body.dataset.immersive === 'true'
  );

  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const obs = new MutationObserver(() => {
      setImmersiveBodyFlag(document.body.dataset.immersive === 'true');
    });
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-immersive'] });
    return () => obs.disconnect();
  }, []);

  if (immersiveBodyFlag) return null;
  if (HIDE_ON_EXACT.has(pathname)) return null;
  if (HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) return null;

  const handleBack = () => smartBack(navigate, pathname);
  const label = parentLabelFor(pathname);

  return (
    <button
      onClick={handleBack}
      aria-label={`Back to ${label}`}
      data-testid="global-back-btn"
      className="fixed top-3 left-3 z-50 flex items-center gap-1 px-3 py-2 rounded-full backdrop-blur-md transition-all active:scale-95 hover:bg-white/10 max-w-[55vw]"
      style={{
        background: "rgba(20, 28, 44, 0.55)",
        border: "1px solid rgba(255,255,255,0.10)",
        boxShadow: "0 4px 18px -8px rgba(0,0,0,0.4)",
      }}
    >
      <ChevronLeft size={18} className="text-white/85 flex-shrink-0" />
      <span className="text-[11px] tracking-[0.14em] uppercase text-white/85 font-medium pr-1 truncate">
        {label}
      </span>
    </button>
  );
}
